/**
 * PlotTrack grid helpers (#817, #2041).
 * A missing cell is empty. Absent is its own stored state.
 * Setup/payoff are Chekhov-only. Absent is allowed on every row.
 * No ready field.
 */
import {
  CHEKHOV_HIDDEN,
  CHEKHOV_HIDDEN_HINT,
  CHEKHOV_OBVIOUS,
  CHEKHOV_OBVIOUS_HINT,
  CHEKHOV_OPEN,
  CHEKHOV_OPEN_HINT,
} from './writerCopy';

export const CELL_STATES = ['active', 'quiet', 'setup', 'payoff', 'empty', 'absent'];
export const ANY_ROW_STATES = ['active', 'quiet', 'empty', 'absent'];
export const CHEKHOV_ONLY_STATES = ['setup', 'payoff'];
export const CHEKHOV_VISIBILITIES = ['hidden', 'open', 'obvious'];
export const FORBIDDEN_READY_KEYS = ['ready', 'readiness', 'at_risk', 'at-risk'];
export const ADDABLE_ROW_TYPES = ['subplot', 'chekhov_promise'];
export const CHEKHOV_VISIBILITY_COPY = {
  hidden: { value: 'hidden', label: CHEKHOV_HIDDEN, hint: CHEKHOV_HIDDEN_HINT },
  open: { value: 'open', label: CHEKHOV_OPEN, hint: CHEKHOV_OPEN_HINT },
  obvious: { value: 'obvious', label: CHEKHOV_OBVIOUS, hint: CHEKHOV_OBVIOUS_HINT },
};

export function chekhovVisibilityCopy(value) {
  return CHEKHOV_VISIBILITY_COPY[value] || CHEKHOV_VISIBILITY_COPY.hidden;
}

export function hasMainRow(rows) {
  return (rows || []).some((row) => row?.type === 'main');
}

export function addableRowTypes(rows) {
  return hasMainRow(rows) ? ADDABLE_ROW_TYPES : ['main', ...ADDABLE_ROW_TYPES];
}

export function emptyCell(rowId, columnId) {
  return {
    id: null,
    row_id: rowId,
    column_id: columnId,
    state: 'empty',
    note: '',
    beats: [],
    stored: false,
  };
}

export function cellAt(cells, rowId, columnId) {
  const found = (cells || []).find(
    (cell) => String(cell.row_id) === String(rowId) && String(cell.column_id) === String(columnId),
  );
  if (!found) return emptyCell(rowId, columnId);
  const cell = { ...emptyCell(rowId, columnId), ...found, stored: found.stored !== false };
  if (cell.state === 'absent') return { ...cell, note: '', beats: [] };
  return cell;
}

export function allowedStatesForRow(rowType) {
  if (rowType === 'chekhov_promise') return CELL_STATES;
  return ANY_ROW_STATES;
}

export function isChekhovRow(row) {
  return row?.type === 'chekhov_promise';
}

/** Subplot and Chekhov rows keep a writer-chosen colour. Main does not. */
export function rowCanPickColor(row) {
  return row?.type === 'subplot' || row?.type === 'chekhov_promise';
}

/** `<input type="color">` only accepts #rrggbb. */
export function colorInputValue(color) {
  const raw = String(color || '').trim();
  return /^#[0-9a-fA-F]{6}$/.test(raw) ? raw : '#888888';
}

export function cellHasContent(cell) {
  if (!cell || cell.state === 'absent') return false;
  if (String(cell.note || '').trim()) return true;
  return (cell.beats || []).some((beat) => String(beat || '').trim());
}

export function rejectReadyFields(payload) {
  if (!payload) return;
  Object.keys(payload).forEach((key) => {
    if (FORBIDDEN_READY_KEYS.includes(String(key).toLowerCase())) {
      throw new Error('Ready is not a PlotTrack row or cell field');
    }
  });
}

export function columnSceneIds(columns) {
  return (columns || []).map((column) => String(column.scene_id));
}

export function headerSpan(header, columns) {
  const ids = new Set((header.scene_ids || []).map((id) => String(id)));
  if (ids.size === 0) return 0;
  return (columns || []).filter((column) => ids.has(String(column.scene_id))).length;
}

/** Same bar as backend `_text_is_sequence_sized`. */
export const SEQUENCE_FILL_CHARS = 1600;
export const FAT_PARAGRAPH_CHARS = 120;
/** Economics-sized cells: several beats, or a long write-up, still one column. */
export const MULTI_BEAT_PACK = 4;
export const FAT_PACK_CHARS = 500;

export function textIsSequenceSized(text) {
  const raw = String(text || '').trim();
  const matches = raw.match(new RegExp('\\bscene\\s+\\d+\\b', 'gi'));
  if (matches && matches.length >= 2) return true;
  const paragraphs = raw
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter((part) => part.length >= FAT_PARAGRAPH_CHARS);
  return raw.length >= SEQUENCE_FILL_CHARS && paragraphs.length >= 3;
}

export function cellsForColumn(column, cells) {
  if (!column) return [];
  return (cells || []).filter((cell) => (
    String(cell?.column_id) === String(column.id)
    || (column.scene_id && String(cell?.scene_id) === String(column.scene_id))
  ));
}

function beatList(beats) {
  if (Array.isArray(beats)) return beats;
  // A fill phrase sometimes arrives as one string. Do not walk its letters.
  if (typeof beats === 'string' && beats !== '') return [beats];
  return [];
}

export function columnBeatLines(cells) {
  const lines = [];
  (cells || []).forEach((cell) => {
    if (cell?.state === 'absent') return;
    beatList(cell?.beats).forEach((beat) => {
      const text = typeof beat === 'string'
        ? beat.trim()
        : String(beat?.action || beat?.text || '').trim();
      if (text) lines.push(text);
    });
  });
  return lines;
}

export function columnPlainText(column, cells) {
  const parts = [];
  const seen = new Set();
  const push = (value) => {
    const text = String(value || '').trim();
    if (!text || seen.has(text)) return;
    seen.add(text);
    parts.push(text);
  };
  const name = String(column?.display_name || '').trim();
  if (name && name !== 'Untitled scene' && name !== 'Untitled sequence') push(name);
  push(column?.metadata?.intention);
  (cells || []).forEach((cell) => {
    if (cell?.state === 'absent') return;
    push(cell?.note);
  });
  columnBeatLines(cells).forEach(push);
  return parts.join('\n\n');
}

/** Offer grain when it was stored. Otherwise the sequence-sized text heuristic. */
export function columnIsSequence(column, cells) {
  if (!column) return false;
  const stored = column.grain || column.metadata?.grain;
  if (stored === 'sequence') return true;
  if (stored === 'scene') return false;
  return textIsSequenceSized(columnPlainText(column, cells));
}

/** "Scene 1" is a generated placeholder. "Scene 2.2" is a real title. */
export function isPlaceholderSceneName(name) {
  return /^scene\s*\d+$/i.test(String(name || '').trim());
}

export function columnDisplayName(column, cells) {
  const sequence = columnIsSequence(column, cells);
  const name = String(column?.display_name || '').trim();
  if (!name || name === 'Untitled scene' || name === 'Untitled sequence' || isPlaceholderSceneName(name)) {
    return sequence ? 'Untitled sequence' : 'Untitled scene';
  }
  return name;
}

/**
 * Warn before Draft writes a fat, multi-beat, or sequence-sized column
 * as one scene of prose. Ordinary short scenes stay quiet.
 */
export function columnNeedsDraftGate(column, cells) {
  const related = cellsForColumn(column, cells);
  if (columnIsSequence(column, related)) return true;
  if (textIsSequenceSized(columnPlainText(column, related))) return true;
  if (columnBeatLines(related).length >= MULTI_BEAT_PACK) return true;
  return columnPlainText(column, related).trim().length >= FAT_PACK_CHARS;
}

export function sceneLineLabel(column, index) {
  const position = Number(index) + 1;
  const name = columnDisplayName(column);
  return `${position} · ${name}`;
}

export function linePosition(columns, sceneId) {
  const index = (columns || []).findIndex((column) => String(column.scene_id) === String(sceneId));
  return index;
}

export function headerEndpoints(header, columns) {
  const ids = (header?.scene_ids || []).map((id) => String(id));
  const onLine = (columns || []).filter((column) => ids.includes(String(column.scene_id)));
  if (onLine.length === 0) {
    return {
      startSceneId: columns?.[0]?.scene_id || '',
      endSceneId: columns?.[columns.length - 1]?.scene_id || '',
    };
  }
  return {
    startSceneId: onLine[0].scene_id,
    endSceneId: onLine[onLine.length - 1].scene_id,
  };
}

export function rowLabel(row) {
  return row?.label || (row?.type === 'main' ? 'Main plot' : row?.type === 'chekhov_promise' ? 'Chekhov Promise' : 'Subplot');
}

/**
 * Whose head (#858 / #1133) is a name on the scene column plus a filter.
 * The field stays `pov`. No second color. Do not invent ready.
 * Writer-chosen SoT is Not set / All / a person — beats do not fill it.
 */
export const WHOSE_HEAD_CHOICE_NOT_SET = 'not_set';
export const WHOSE_HEAD_CHOICE_ALL = 'all';
export const WHOSE_HEAD_CHOICE_PERSON = 'person';

export function whoseHeadName(column) {
  return (column?.pov?.entity_name || '').trim();
}

export function whoseHeadChoice(column) {
  const pov = column?.pov;
  if (!pov || typeof pov !== 'object') return WHOSE_HEAD_CHOICE_NOT_SET;
  if (pov.choice === WHOSE_HEAD_CHOICE_ALL || pov.all === true) {
    return WHOSE_HEAD_CHOICE_ALL;
  }
  if (pov.choice === WHOSE_HEAD_CHOICE_NOT_SET) return WHOSE_HEAD_CHOICE_NOT_SET;
  if (pov.choice === WHOSE_HEAD_CHOICE_PERSON) return WHOSE_HEAD_CHOICE_PERSON;
  if (whoseHeadId(column) || whoseHeadName(column)) return WHOSE_HEAD_CHOICE_PERSON;
  return WHOSE_HEAD_CHOICE_NOT_SET;
}

export function whoseHeadReadout(column) {
  const choice = whoseHeadChoice(column);
  if (choice === WHOSE_HEAD_CHOICE_ALL) return 'All';
  if (choice === WHOSE_HEAD_CHOICE_PERSON) return whoseHeadName(column);
  return '';
}

export function whoseHeadValue(column) {
  const choice = whoseHeadChoice(column);
  return {
    choice,
    entityId: whoseHeadId(column),
    entityName: whoseHeadName(column),
  };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const NOT_A_PERSON = new Set([
  'The', 'A', 'An', 'This', 'That', 'These', 'Those',
  'After', 'Before', 'When', 'Then', 'And', 'But', 'As',
  'In', 'On', 'At', 'With', 'From', 'For', 'His', 'Her',
  'Their', 'She', 'He', 'They', 'It', 'We', 'You',
]);

function isUuid(value) {
  return UUID_RE.test(String(value || '').trim());
}

function asPersonName(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  const name = String(value).trim();
  if (!name || isUuid(name) || NOT_A_PERSON.has(name)) return '';
  return name;
}

/** First given-name at the start of a beat — “Win reviews…”, not “The Assignment”. */
export function leadingProperName(text) {
  const match = String(text || '').trim().match(/^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\b/);
  return match ? asPersonName(match[1]) : '';
}

export function viewpointFromBeats(beats) {
  const counts = new Map();
  (beats || []).forEach((beat) => {
    const raw = typeof beat === 'string'
      ? beat
      : (beat?.who || beat?.action || beat?.text || '');
    const namedWho = asPersonName(typeof beat === 'object' && beat ? beat.who : '');
    const name = namedWho || leadingProperName(raw);
    if (!name) return;
    counts.set(name, (counts.get(name) || 0) + 1);
  });
  let best = '';
  let bestCount = 0;
  counts.forEach((count, name) => {
    if (count > bestCount) {
      best = name;
      bestCount = count;
    }
  });
  return best;
}

export function viewpointFromCell(cell) {
  if (!cell) return '';
  const direct = asPersonName(cell.who || cell.viewpoint || cell.pov);
  if (direct) return direct;
  const fromBeats = viewpointFromBeats(cell.beats);
  if (fromBeats) return fromBeats;
  return leadingProperName(cell.note);
}

/**
 * WR Workshop Walk columns have pov.entity_name = null. Viewpoint lives
 * on the cell beats (Win is the subject of The Assignment). Formal POV
 * still wins when the map actually named someone.
 */
export function viewpointFromColumn(column) {
  if (!column) return '';
  const pov = column.pov;
  if (typeof pov === 'string') {
    const named = asPersonName(pov);
    if (named) return named;
  }
  const name = whoseHeadName(column);
  if (name) return name;
  const labeled = asPersonName(pov?.mode_label || pov?.name || column.pov_mode || column.viewpoint);
  if (labeled) return labeled;
  const meta = column.metadata || {};
  for (const key of ['viewpoint', 'viewpoint_character', 'whose_head', 'pov_name', 'pov']) {
    const named = asPersonName(meta[key] || column[key]);
    if (named) return named;
  }
  const chars = meta.characters || column.characters;
  if (Array.isArray(chars) && chars[0]) {
    const first = chars[0];
    const named = asPersonName(first?.name || first?.entity_name || first);
    if (named) return named;
  }
  return '';
}

export function viewpointFromOutline({ column, cell, cells, threads } = {}) {
  const formal = viewpointFromColumn(column);
  if (formal) return formal;
  const fromCell = viewpointFromCell(cell);
  if (fromCell) return fromCell;
  for (const extra of cells || []) {
    const named = viewpointFromCell(extra);
    if (named) return named;
  }
  for (const thread of threads || []) {
    const named = viewpointFromBeats(thread?.beats) || leadingProperName(thread?.note);
    if (named) return named;
  }
  return '';
}

export function columnHandoff(column, cell, cells) {
  const choice = whoseHeadChoice(column);
  let viewpoint = '';
  if (choice === WHOSE_HEAD_CHOICE_ALL) viewpoint = 'All';
  else if (choice === WHOSE_HEAD_CHOICE_PERSON) viewpoint = whoseHeadName(column);
  return {
    sceneId: column?.scene_id || '',
    title: String(column?.display_name || '').trim(),
    viewpoint,
  };
}

export function whoseHeadId(column) {
  const id = column?.pov?.entity_id;
  return id ? String(id) : '';
}

export function whoseHeadOptions(columns) {
  const seen = new Map();
  (columns || []).forEach((column) => {
    const id = whoseHeadId(column);
    const name = whoseHeadName(column);
    if (!id || !name || seen.has(id)) return;
    seen.set(id, name);
  });
  return Array.from(seen.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function filterColumnsByWhoseHead(columns, whoseHeadIdFilter) {
  if (!whoseHeadIdFilter) return columns || [];
  const wanted = String(whoseHeadIdFilter);
  return (columns || []).filter((column) => whoseHeadId(column) === wanted);
}
